import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Tag, Divider, Alert, Collapse } from 'antd';
import { CodeOutlined, DatabaseOutlined, BranchesOutlined, ToolOutlined, SaveOutlined, QuestionCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

interface CodeExample {
  name: string;
  code: string;
  description: string;
}

interface DDDConcept {
  definition: string;
  examples: CodeExample[];
  characteristics: string[];
  whyNeed: string;
  note?: string;
}

interface DDDConcepts {
  valueObjects: DDDConcept;
  entities: DDDConcept;
  aggregates: DDDConcept;
  domainServices: DDDConcept;
  repositories: DDDConcept;
}

const ConceptExplainer: React.FC = () => {
  const [concepts, setConcepts] = useState<DDDConcepts | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConcepts();
  }, []);

  const fetchConcepts = async () => {
    try {
      const response = await fetch(`/api/ddd/concepts`);
      const data = await response.json();
      setConcepts(data);
    } catch (error) {
      console.error('获取DDD概念失败:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>加载中...</div>;
  }

  if (!concepts) {
    return <Alert message="无法加载DDD概念，请检查后端服务是否启动" type="error" />;
  }

  const conceptCards = [
    {
      key: 'valueObjects',
      title: '值对象 (Value Objects)',
      icon: <CodeOutlined style={{ color: '#52c41a' }} />,
      color: '#f6ffed',
      borderColor: '#b7eb8f',
      data: concepts.valueObjects,
      priority: 1
    },
    {
      key: 'entities',
      title: '实体 (Entities)',
      icon: <DatabaseOutlined style={{ color: '#1890ff' }} />,
      color: '#f0f5ff',
      borderColor: '#91d5ff',
      data: concepts.entities,
      priority: 1
    },
    {
      key: 'aggregates',
      title: '聚合 (Aggregates)',
      icon: <BranchesOutlined style={{ color: '#722ed1' }} />,
      color: '#f9f0ff',
      borderColor: '#d3adf7',
      data: concepts.aggregates,
      priority: 1
    },
    {
      key: 'domainServices',
      title: '领域服务 (Domain Services)',
      icon: <ToolOutlined style={{ color: '#fa8c16' }} />,
      color: '#fff7e6',
      borderColor: '#ffd591',
      data: concepts.domainServices,
      priority: 1
    },
    {
      key: 'repositories',
      title: '数据库访问层 (Repository)',
      icon: <SaveOutlined style={{ color: '#eb2f96' }} />,
      color: '#fff0f6',
      borderColor: '#ffadd2',
      data: concepts.repositories,
      priority: 2 // 标记为次要概念
    }
  ];

  return (
    <div>
      <Title level={2}>DDD核心概念解释</Title>
      
      <Alert
        message="关于数据库访问层(Repository)的说明"
        description="严格来说，数据库访问层属于基础设施层的技术概念，不是核心领域概念。但在DDD实践中经常一起讨论，因为它帮助领域层保持纯净。"
        type="info"
        icon={<QuestionCircleOutlined />}
        showIcon
        style={{ marginBottom: 24 }}
      />
      
      <Paragraph>
        领域驱动设计（Domain-Driven Design, DDD）是一种软件开发方法论，强调将业务领域的复杂性通过模型来表达。
        以下是DDD中的<strong>核心领域概念</strong>和<strong>技术支撑概念</strong>，通过实际代码案例来帮助理解。
      </Paragraph>
      
      <Title level={4} style={{ color: '#1890ff', marginTop: 32 }}>
        核心领域概念 (Core Domain Concepts)
      </Title>

      {/* 核心领域概念 */}
      <Row gutter={[16, 16]}>
        {conceptCards.filter(card => card.priority === 1).map((card) => (
          <Col span={12} key={card.key}>
            <Card
              title={
                <span>
                  {card.icon}
                  <span style={{ marginLeft: 8 }}>{card.title}</span>
                </span>
              }
              style={{ 
                backgroundColor: card.color,
                borderColor: card.borderColor,
                height: '100%'
              }}
              size="small"
            >
              <div style={{ marginBottom: 12 }}>
                <Text strong>定义：</Text>
                <Paragraph style={{ marginTop: 4 }}>{card.data.definition}</Paragraph>
              </div>

              <div style={{ marginBottom: 12 }}>
                <Text strong style={{ color: '#1890ff' }}>为什么需要？</Text>
                <Paragraph style={{ marginTop: 4, fontStyle: 'italic', color: '#666' }}>
                  {card.data.whyNeed}
                </Paragraph>
              </div>

              <Collapse ghost size="small">
                <Panel header="📝 代码示例" key="examples">
                  {card.data.examples.map((example, index) => (
                    <div key={index} style={{ marginBottom: index < card.data.examples.length - 1 ? 16 : 0 }}>
                      <Text strong style={{ color: '#722ed1' }}>{example.name}</Text>
                      <pre style={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: 12, 
                        borderRadius: 4, 
                        fontSize: 12,
                        overflow: 'auto',
                        marginTop: 8
                      }}>
                        {example.code}
                      </pre>
                      <Text style={{ color: '#666', fontSize: 13 }}>{example.description}</Text>
                    </div>
                  ))}
                </Panel>
                <Panel header="🏷️ 关键特征" key="characteristics">
                  <div>
                    {card.data.characteristics.map((char, index) => (
                      <Tag key={index} style={{ marginBottom: 4 }}>{char}</Tag>
                    ))}
                  </div>
                </Panel>
              </Collapse>
            </Card>
          </Col>
        ))}
      </Row>

      <Title level={4} style={{ color: '#eb2f96', marginTop: 32 }}>
        技术支撑概念 (Technical Support Concepts)
      </Title>

      {/* 技术支撑概念 */}
      <Row gutter={[16, 16]}>
        {conceptCards.filter(card => card.priority === 2).map((card) => (
          <Col span={24} key={card.key}>
            <Card
              title={
                <span>
                  {card.icon}
                  <span style={{ marginLeft: 8 }}>{card.title}</span>
                  {card.data.note && (
                    <Tag color="orange" style={{ marginLeft: 8 }}>
                      <ExclamationCircleOutlined /> 技术概念
                    </Tag>
                  )}
                </span>
              }
              style={{ 
                backgroundColor: card.color,
                borderColor: card.borderColor,
                borderStyle: 'dashed'  // 用虚线边框区分技术概念
              }}
              size="small"
            >
              <div style={{ marginBottom: 12 }}>
                <Text strong>定义：</Text>
                <Paragraph style={{ marginTop: 4 }}>{card.data.definition}</Paragraph>
              </div>

              {card.data.note && (
                <Alert
                  message={card.data.note}
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                />
              )}

              <div style={{ marginBottom: 12 }}>
                <Text strong style={{ color: '#eb2f96' }}>为什么需要？</Text>
                <Paragraph style={{ marginTop: 4, fontStyle: 'italic', color: '#666' }}>
                  {card.data.whyNeed}
                </Paragraph>
              </div>

              <Collapse ghost size="small">
                <Panel header="📝 对比示例" key="examples">
                  {card.data.examples.map((example, index) => (
                    <div key={index} style={{ marginBottom: index < card.data.examples.length - 1 ? 16 : 0 }}>
                      <Text strong style={{ color: '#722ed1' }}>{example.name}</Text>
                      <pre style={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: 12, 
                        borderRadius: 4, 
                        fontSize: 12,
                        overflow: 'auto',
                        marginTop: 8
                      }}>
                        {example.code}
                      </pre>
                      <Text style={{ color: '#666', fontSize: 13 }}>{example.description}</Text>
                    </div>
                  ))}
                </Panel>
                <Panel header="🏷️ 关键特征" key="characteristics">
                  <div>
                    {card.data.characteristics.map((char, index) => (
                      <Tag key={index} style={{ marginBottom: 4 }}>{char}</Tag>
                    ))}
                  </div>
                </Panel>
              </Collapse>
            </Card>
          </Col>
        ))}
      </Row>

      <Divider />
      
      <Alert
        message="学习建议"
        description={
          <ul>
            <li><strong>先掌握核心领域概念</strong>：值对象、实体、聚合、领域服务是DDD的精髓</li>
            <li><strong>理解概念间关系</strong>：值对象描述特征，实体代表"东西"，聚合管理一致性，服务处理协作逻辑</li>
            <li><strong>数据库访问层是辅助概念</strong>：帮助领域层与数据存储解耦，但不是业务建模的核心</li>
            <li><strong>从实际业务出发</strong>：每个概念都要对应具体的业务需求，不为了技术而技术</li>
          </ul>
        }
        type="success"
        showIcon
        style={{ marginTop: 24 }}
      />
    </div>
  );
};

export default ConceptExplainer;